import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  Sparkles,
  CheckCircle2,
  Users,
  Compass,
  FileText,
  Video,
  BarChart3,
  ArrowRight,
  ShieldCheck,
  Zap,
  Youtube,
  Instagram,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";

const POPULAR_NICHES = [
  "Tech and AI",
  "Content Creation and Growth",
  "Finance and Investing",
  "Health and Fitness",
  "Education and Careers",
  "Gaming",
  "Lifestyle and Vlogs",
  "Business and Startups",
  "Other",
];

const PLATFORM_OPTIONS = [
  { id: "youtube", label: "YouTube" },
  { id: "instagram", label: "Instagram" },
];

export default function Waitlist() {
  usePageTitle("Join the Waitlist");
  const [email, setEmail] = useState("");
  const [niche, setNiche] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState(["youtube", "instagram"]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [position, setPosition] = useState(null);
  const [totalCount, setTotalCount] = useState(142);
  const [activeTab, setActiveTab] = useState("intelligence");

  useEffect(() => {
    api
      .get("/waitlist/stats")
      .then(({ data }) => {
        if (data?.total_count) {
          setTotalCount(Math.max(140, data.total_count + 138));
        }
      })
      .catch(() => {});
  }, []);

  const togglePlatform = (id) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/waitlist", {
        email: email.trim(),
        niche: niche || null,
        platforms: selectedPlatforms,
        notes: notes.trim() || null,
      });

      setSubmitted(true);
      setPosition(data.position || totalCount + 1);
      toast.success(data.message || "You have been added to the early access list.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-accent/30 selection:text-accent">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="text-xs sm:text-sm font-medium text-gray-600 transition-colors hover:text-gray-900">
              Sign in
            </Link>
            <a href="#join-form" className="rounded-full bg-gray-900 px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-semibold text-white transition-all hover:bg-gray-700">
              Join waitlist
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-12 sm:pt-16 sm:pb-16 md:pt-24 md:pb-20">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-25">
          <div className="h-[480px] w-[480px] rounded-full bg-accent/20 blur-[130px]" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 sm:px-3.5 py-1 sm:py-1.5 text-xs text-muted-foreground shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-accent animate-pulse" />
            <span>Early creator access is open</span>
          </div>

          <h1 className="mt-5 sm:mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-5xl md:text-6xl text-foreground">
            The complete content engine for all your platforms.
          </h1>

          <p className="mx-auto mt-4 sm:mt-6 max-w-2xl text-sm sm:text-base text-muted-foreground sm:text-lg leading-relaxed">
            We handle the market research, find high-performing topics, and generate opening hooks that hold attention. You get structured scripts matched to your personal voice, ready to record and publish across your channels.
          </p>

          {/* Platform Pills */}
          <div className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 sm:px-3.5 py-1 sm:py-1.5 text-xs font-semibold text-foreground shadow-sm">
              <Youtube className="h-4 w-4 text-red-500" /> YouTube
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 sm:px-3.5 py-1 sm:py-1.5 text-xs font-semibold text-foreground shadow-sm">
              <Instagram className="h-4 w-4 text-pink-500" /> Instagram
            </span>
          </div>

          {/* Quick Counter */}
          <div className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-accent" />
              <span>{totalCount} creators in line</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span>Free early access</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-accent" />
              <span>No credit card required</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Waitlist Form Section */}
      <section id="join-form" className="relative mx-auto max-w-xl px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-border bg-surface p-5 sm:p-8 shadow-xl shadow-black/40">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="font-display text-xl font-bold text-foreground">Get early access</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  We are letting creators in in batches. Leave your email below to reserve your spot.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Email address <span className="text-accent">*</span>
                </label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@creator.com"
                  className="bg-background text-sm"
                  data-testid="waitlist-email-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  Content niche (optional)
                </label>
                <select
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Select your main niche</option>
                  {POPULAR_NICHES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-2">
                  Platforms you create for (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_OPTIONS.map((p) => {
                    const isSelected = selectedPlatforms.includes(p.id);
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => togglePlatform(p.id)}
                        className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                          isSelected
                            ? "bg-accent text-accent-foreground shadow-sm"
                            : "border border-border bg-background text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1.5">
                  What is your biggest content bottleneck? (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Finding topics people care about, writing scripts fast, camera setup..."
                  rows={3}
                  className="w-full rounded-md border border-border bg-background p-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-accent placeholder:text-muted-foreground/60"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-accent py-2.5 font-bold text-accent-foreground hover:bg-accent-hover transition-all"
                data-testid="waitlist-submit-button"
              >
                {submitting ? "Reserving your spot..." : "Join early access waitlist"}
              </Button>
            </form>
          ) : (
            <div className="py-6 text-center space-y-4">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <h3 className="font-display text-2xl font-bold text-foreground">You are on the list!</h3>
              <p className="text-sm text-muted-foreground">
                We saved your email <span className="font-medium text-foreground">{email}</span>. Your priority position is:
              </p>
              <div className="inline-block rounded-xl border border-accent/40 bg-accent/10 px-6 py-3">
                <span className="text-xs uppercase tracking-widest text-accent font-semibold block">Spot</span>
                <span className="text-3xl font-extrabold text-accent">#{position || totalCount + 1}</span>
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                We will email you as soon as your batch opens up.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Interactive App Preview Section */}
      <section className="mx-auto max-w-6xl px-6 py-16 border-t border-border/60">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Product tour</p>
          <h2 className="mt-2 font-display text-3xl font-bold text-foreground sm:text-4xl">
            Everything you need in one creator workflow
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            From discovering topics to writing scripts and recording video takes.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {[
            { id: "intelligence",  label: "Market Intelligence",    short: "Intelligence",  icon: Compass   },
            { id: "opportunities", label: "Content Opportunities",  short: "Opportunities", icon: Sparkles  },
            { id: "editor",        label: "Script Studio",          short: "Script",        icon: FileText  },
            { id: "recording",     label: "Studio Teleprompter",    short: "Studio",        icon: Video     },
            { id: "analytics",     label: "Creator Analytics",      short: "Analytics",     icon: BarChart3 },
          ].map((tab, i) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                  active
                    ? "bg-surface border border-accent text-accent shadow-sm"
                    : "border border-border bg-surface/50 text-muted-foreground hover:bg-surface hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">{i + 1}. {tab.label}</span>
                <span className="sm:hidden">{tab.short}</span>
              </button>
            );
          })}
        </div>

        {/* Feature Preview Canvas */}
        <div className="rounded-2xl border border-border bg-surface p-6 md:p-8 shadow-2xl shadow-black/50">
          {activeTab === "intelligence" && (
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                  <Compass className="h-3.5 w-3.5" />
                  <span>Module 1: Market Intelligence</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Find out what people are searching for before you hit record
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Virality connects to YouTube data and creator discussions in your niche. It surfaces questions with high search volume, emerging trends, and audience pain points so you never run out of ideas.
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground pt-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Real questions extracted directly from comment sections.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Rising and declining topic indicators for your niche.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Content gaps where competitors have not answered viewer questions.</span>
                  </li>
                </ul>
              </div>

              {/* UI Mockup Card */}
              <div className="rounded-xl border border-border bg-background p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-accent" />
                    <span className="text-xs font-semibold text-foreground">Niche Intelligence: AI in Finance & Investing</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground">Updated 10m ago</span>
                </div>

                <div className="space-y-3">
                  <div className="rounded-lg border border-border/80 bg-surface p-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-foreground">How AI Hedge Funds Pick Stocks in 2026</span>
                      <span className="rounded bg-accent/15 px-2 py-0.5 text-[10px] font-bold text-accent">Rising +84%</span>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      46 comment questions asking about algorithmic screening tools and retail risk models.
                    </p>
                  </div>

                  <div className="rounded-lg border border-border/80 bg-surface p-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-foreground">AI Agents for Personal Wealth Management</span>
                      <span className="rounded bg-accent/15 px-2 py-0.5 text-[10px] font-bold text-accent">High Demand</span>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Audience asking which autonomous portfolio rebalancing platforms are actually safe.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "opportunities" && (
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Module 2: Content Opportunities</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Ranked video topics scored by real demand
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Each opportunity comes with an estimated demand score, growth velocity, and the exact audience reasoning behind why this topic will perform now.
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground pt-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>0 to 100 Opportunity Score ranking based on audience signals.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Suggested hook angles and specific format recommendations.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>One click conversion into a full script draft.</span>
                  </li>
                </ul>
              </div>

              {/* UI Mockup Card */}
              <div className="rounded-xl border border-border bg-background p-5 space-y-3">
                <div className="rounded-lg border border-border bg-surface p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="rounded bg-elevated px-2 py-0.5 text-[10px] font-semibold text-muted-foreground uppercase">
                        Strategy / Deep Dive
                      </span>
                      <h4 className="mt-2 text-sm font-bold text-foreground">
                        How Top Investors Use DeepSeek and Gemini for Market Research
                      </h4>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-extrabold text-accent">96/100</div>
                      <span className="text-[10px] text-muted-foreground">Score</span>
                    </div>
                  </div>
                  <p className="mt-2.5 text-xs text-muted-foreground">
                    Strong search interest over the last 14 days with zero clear creator breakdowns on practical prompts.
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground border-t border-border/50 pt-2.5">
                    <span>Demand: <strong className="text-foreground">95</strong></span>
                    <span>Growth: <strong className="text-foreground">92</strong></span>
                    <span>Evidence: <strong className="text-foreground">16 signals</strong></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "editor" && (
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                  <FileText className="h-3.5 w-3.5" />
                  <span>Module 3: Script Studio</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Draft engaging scripts matched to your speaking style
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Turn an idea into a structured script with an attention-grabbing hook, clear core sections, and natural call to actions.
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground pt-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Target duration estimates and live word counts.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Hook variation suggestions for YouTube and short form reels.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Instant transfer to the recording studio teleprompter.</span>
                  </li>
                </ul>
              </div>

              {/* UI Mockup Card */}
              <div className="rounded-xl border border-border bg-background p-5 space-y-3 font-mono text-xs">
                <div className="flex items-center justify-between border-b border-border pb-2 text-muted-foreground">
                  <span>Script Editor</span>
                  <span>Estimated: 2m 45s</span>
                </div>
                <div className="space-y-2 font-sans leading-relaxed text-foreground">
                  <p className="text-accent font-semibold text-xs">[HOOK - 0:00]</p>
                  <p className="text-xs bg-surface p-2.5 rounded border border-border">
                    "Most people think AI in finance is just ChatGPT for stock tips, but hedge funds are using something completely different."
                  </p>
                  <p className="text-accent font-semibold text-xs pt-1">[BODY - 0:20]</p>
                  <p className="text-xs bg-surface p-2.5 rounded border border-border">
                    "Here is the exact 3-step workflow modern analysts use to find undervalued assets in minutes."
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "recording" && (
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                  <Video className="h-3.5 w-3.5" />
                  <span>Module 4: Camera Studio and Teleprompter</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Record natural takes without memorizing long scripts
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Keep natural eye contact with your camera while your script scrolls at your preferred talking pace. Control font size, scroll speed, and prompter opacity to deliver confident videos on the first take.
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground pt-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /><span>Smooth scrolling prompter synchronized with your speaking speed.</span></li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /><span>High definition camera recording with instant take playback.</span></li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /><span>Direct media management to organize takes for every project.</span></li>
                </ul>
              </div>

              {/* UI Mockup Card — studio feel with dark intentionally */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-gray-900 p-6 flex flex-col items-center justify-center min-h-[220px] text-center">
                <div className="relative z-10 space-y-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white shadow-lg">
                    <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                    <span>REC 01:24</span>
                  </div>
                  <p className="text-sm font-medium text-white max-w-xs">
                    "Most people think AI in finance is just ChatGPT for stock tips, but top analysts use a completely different framework..."
                  </p>
                  <div className="pt-2">
                    <span className="rounded-full bg-accent/20 px-2.5 py-1 text-[11px] font-bold text-accent border border-accent/30">
                      Teleprompter Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "analytics" && (
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                  <BarChart3 className="h-3.5 w-3.5" />
                  <span>Module 5: Multi-Platform Analytics</span>
                </div>
                <h3 className="font-display text-2xl font-bold text-foreground">
                  Track YouTube and Instagram growth in one clean dashboard
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Connect your accounts to view subscriber trends, watch time in hours and minutes, reel interactions, and engagement rates without jumping between multiple apps.
                </p>
                <ul className="space-y-2 text-xs text-muted-foreground pt-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Dedicated YouTube Studio view with watch hours and velocity charts.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Instagram Creator gallery showing reel likes, comments, and engagement.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span>Cross-platform summary of your total audience reach.</span>
                  </li>
                </ul>
              </div>

              {/* UI Mockup Card */}
              <div className="rounded-xl border border-border bg-background p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-border bg-surface p-3.5">
                    <div className="flex items-center gap-1.5 text-xs text-red-400 font-semibold">
                      <Youtube className="h-3.5 w-3.5" /> YouTube
                    </div>
                    <div className="mt-2 text-xl font-bold text-foreground">14.2k</div>
                    <p className="text-[11px] text-muted-foreground">Views (last 28d)</p>
                  </div>
                  <div className="rounded-lg border border-border bg-surface p-3.5">
                    <div className="flex items-center gap-1.5 text-xs text-pink-400 font-semibold">
                      <Instagram className="h-3.5 w-3.5" /> Instagram
                    </div>
                    <div className="mt-2 text-xl font-bold text-foreground">8.4%</div>
                    <p className="text-[11px] text-muted-foreground">Engagement rate</p>
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-surface p-3 text-xs text-muted-foreground flex items-center justify-between">
                  <span>Synced accounts: @virality53, Tech Channel</span>
                  <span className="text-accent font-semibold">Live sync</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="mx-auto max-w-4xl px-6 py-16 border-t border-border/60">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
            Frequently asked questions
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-surface p-5">
            <h3 className="text-sm font-bold text-foreground">When will I get access?</h3>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              We are onboarding creators in small weekly groups to ensure smooth support and server reliability. Early waitlist members get first priority.
            </p>
          </div>

          <div className="rounded-xl border border-border bg-surface p-5">
            <h3 className="text-sm font-bold text-foreground">Is it free during early access?</h3>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              Yes. Early access members get free access to all modules, intelligence scans, and teleprompter recording tools.
            </p>
          </div>

          <div className="rounded-xl border border-border bg-surface p-5">
            <h3 className="text-sm font-bold text-foreground">What platforms are supported?</h3>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              YouTube and Instagram are fully integrated with live OAuth connections and performance analytics.
            </p>
          </div>

          <div className="rounded-xl border border-border p-5 bg-surface">
            <h3 className="text-sm font-bold text-foreground">Do my recordings stay private?</h3>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              Yes. Your video recordings are processed directly on your device and saved to your local browser storage until you download the MP4.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="mx-auto max-w-4xl px-6 py-16 text-center">
        <div className="rounded-2xl border border-accent/30 bg-accent/5 p-8 sm:p-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Ready to simplify your content creation?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Join other creators who are planning and recording their videos with Virality.
          </p>
          <div className="mt-6">
            <a
              href="#join-form"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-bold text-accent-foreground hover:bg-accent-hover transition-all shadow-lg shadow-accent/20"
            >
              <span>Join the waitlist</span>
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60 py-8 text-center text-xs text-muted-foreground">
        <div className="mx-auto max-w-6xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo showText={false} className="h-5 w-5" />
            <span>Virality &copy; {new Date().getFullYear()}. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
            <Link to="/login" className="hover:text-foreground">Creator login</Link>
            <Link to="/admin" className="hover:text-foreground inline-flex items-center gap-1">
              <Lock className="h-3 w-3" /> Admin portal
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
